// Dual-track TX/RX sensing-element generator for the printed inductive encoder.
//
// Pure TypeScript. No Angular / DI imports. Deterministic (no Date/random).
// Angles are radians; math frame is y-up; the electrical datum is -pi/2.
//
// This is the geometry that makes the sensor work. The RX coils are differential
// sinusoidal windings (figure-eight ribbons) so that the spatially-uniform TX
// carrier links ~zero net flux (TX rejection) while the periodic rotor target
// modulates the lobes into a sin/cos position signal. The TX is a nested planar
// SPIRAL of a few concentric turns, placed entirely OUTSIDE the fine RX band, and
// its inductance is estimated with a mutual-coupling-aware Wheeler current-sheet
// form and tuned into the chip's LC-tank window.
//
// TX topology (amended 2026-07-10, human-approved): the original construction was
// nested single-turn annular loops whose inductance was a LINEAR SUM of single-
// loop closed forms — that ignores turn-to-turn mutual coupling (real nested turns
// couple as L ~ N^2), under-estimating L by ~10x, and it packed ~15 turns radially
// that overflowed into the RX annulus on the shared 'down' layer (a galvanic
// short). The fix: a compact N-turn spiral confined to the outward-only TX wrap
// (outside fine.rOut) whose inductance is the Wheeler current-sheet estimate.
//
// Construction (normative, from the task brief):
//   For a track with periodicity p on band [rIn,rOut]:
//     rMid = (rIn+rOut)/2,  A = (rOut-rIn)/2 - traceWidth/2
//     r±(θ) = rMid ± A·sin(p·θ - datum), sampled at SEGMENTS_PER_PERIOD·p points.
//   The two curves cross at the 2p sinusoid zeros. The coil is the ribbon between
//   them (r+ traced forward, r- traced back, joined at the terminal azimuth): the
//   ribbon self-crosses at every zero, so consecutive lobes wind oppositely and
//   the signed area sums to ~0. At each crossing the r+ conductor jogs to the
//   'up' layer through a via pair so it can pass over r- without shorting.
//   The cos coil is the sin coil ROTATED by π/(2p) (built by rotation, not
//   re-derived, so quadrature holds to float precision).

import { EncoderBands } from './pcb-stator-encoder-bands';
import { CHIP, EncoderNet } from './pcb-stator-encoder-chip';

export interface CoilTrace { net: EncoderNet; layer: 'down' | 'up'; points: Array<[number, number]>; widthMm: number; }
export interface CoilVia { net: EncoderNet; at: [number, number]; drillMm: number; padMm: number; }
export interface SensingElement {
  traces: CoilTrace[];
  vias: CoilVia[];
  terminals: Array<{ net: EncoderNet; at: [number, number] }>;  // where board routing picks up
  txInductanceEstNH: number;
  rxNetAreaMm2: Record<'fineSin' | 'fineCos' | 'coarseSin' | 'coarseCos', number>; // signed net area
}
export interface CoilParams { traceWidthMm: number; clearanceMm: number; rxTurns: number; datumRad: number; }

export const SEGMENTS_PER_PERIOD = 64;

// Fixed via geometry for RX crossovers (not chip-critical; sane 0.3/0.6 mm via).
const VIA_DRILL_MM = 0.3;
const VIA_PAD_MM = 0.6;
// Half-width (in samples) of the up-layer bridge that hops one conductor over
// the other at each crossing. "±2 samples wide" per the brief.
const JOG_SAMPLES = 2;
// Sample offset from the far turn-around (sample N-1, at this coil's amplitude
// peak = the partner's self-crossing) to the RELOCATED series-jumper band-
// crossing azimuth. Must clear the partner's 'up' self-jog bridge (±JOG_SAMPLES
// around its crossing) so the relocated jumper's 'up' hop sees an empty upper
// layer across the band; 3 samples lands cleanly in that window for both the
// fine (1.4°/sample) and coarse (5.6°/sample) tracks (verified against the
// raster DRC). Kept modest so the outer/inner 'down' edge-arcs stay short.
const FINE_JRS = 3;
const COARSE_JRS = 3;
const HOP_MARGIN = 0.9;
// Fine constant-radius arc sampler for the relocated jumper's band-edge legs
// (same ~2°/step chord fidelity as the escape arcs). Endpoints inclusive.
function arcAtRadius(r: number, a0: number, a1: number): Pt[] {
  const span = a1 - a0;
  const n = Math.max(1, Math.ceil(Math.abs(span) / (2 * Math.PI / 180)));
  const pts: Pt[] = [];
  for (let i = 0; i <= n; i++) { const a = a0 + (span * i) / n; pts.push([r * Math.cos(a), r * Math.sin(a)]); }
  return pts;
}
// Samples used to draw each TX circle (cosmetic; inductance is closed-form).
const TX_CIRCLE_SAMPLES = 128;

type Pt = [number, number];

/** Cyclic sign-change (crossing) detector over two per-sample scalar arrays of
 *  equal length N (indices wrap mod N). Returns, for every place where `f-g`
 *  changes sign between adjacent samples, ONE representative sample index (the
 *  sample nearer the zero). An exact zero at a sample is reported at that sample.
 *  Used to find where two RX curves' radii cross so the yielding coil can jog. */
function crossingIndices(f: number[], g: number[]): number[] {
  const N = f.length;
  const out: number[] = [];
  const d = (i: number) => f[i] - g[i];
  for (let i = 0; i < N; i++) {
    const di = d(i), dn = d((i + 1) % N);
    if (di === 0) out.push(i);                                   // exact crossing on-sample
    else if (di * dn < 0) out.push(Math.abs(di) <= Math.abs(dn) ? i : (i + 1) % N);
  }
  return out;
}

/** Emit one RX conductor as a chain of 'down' segments interrupted by 'up'
 *  bridges at each index in `bridges`: the trace runs on 'down', lifts to 'up'
 *  through a via at `c - JOG_SAMPLES`, spans the crossing on 'up', and drops back
 *  to 'down' through a via at `c + JOG_SAMPLES`. This is the SINGLE piece of
 *  crossover machinery shared by (a) the r+ self-crossing jogs (r+ hops over r-
 *  inside one coil) and (b) the yielding (cos) coil's inter-coil jogs (both
 *  conductors hop over the sin coil). `pointAt` maps a mod-N sample index to xy;
 *  the walk covers [idxFrom, idxTo] inclusive (r+ closes at idxTo = N; r- is open
 *  at idxTo = N-1). Bridge spans never overlap for the index sets used here
 *  (min crossing spacing 8 samples >> 2·JOG_SAMPLES). */
function emitBridgedConductor(
  pointAt: (idx: number) => Pt, idxFrom: number, idxTo: number,
  bridges: number[], net: EncoderNet, w: number, N: number,
): { traces: CoilTrace[]; vias: CoilVia[] } {
  const isBridge = (i: number) => bridges.some(c => i > c - JOG_SAMPLES && i < c + JOG_SAMPLES);
  const isEntry = (i: number) => bridges.some(c => i === c - JOG_SAMPLES);
  const isExit = (i: number) => bridges.some(c => i === c + JOG_SAMPLES);
  const traces: CoilTrace[] = [];
  const vias: CoilVia[] = [];
  let downSeg: Pt[] = [];
  let upSeg: Pt[] = [];
  for (let i = idxFrom; i <= idxTo; i++) {
    const xy = pointAt(((i % N) + N) % N);
    if (isEntry(i)) {                        // close 'down', via up, open 'up' bridge
      downSeg.push(xy);
      if (downSeg.length > 1) traces.push({ net, layer: 'down', points: downSeg, widthMm: w });
      vias.push({ net, at: xy, drillMm: VIA_DRILL_MM, padMm: VIA_PAD_MM });
      downSeg = []; upSeg = [xy];
    } else if (isExit(i)) {                   // close 'up', via down, reopen 'down'
      upSeg.push(xy);
      if (upSeg.length > 1) traces.push({ net, layer: 'up', points: upSeg, widthMm: w });
      vias.push({ net, at: xy, drillMm: VIA_DRILL_MM, padMm: VIA_PAD_MM });
      upSeg = []; downSeg = [xy];
    } else if (isBridge(i)) {
      upSeg.push(xy);
    } else {
      downSeg.push(xy);
    }
  }
  if (downSeg.length > 1) traces.push({ net, layer: 'down', points: downSeg, widthMm: w });
  if (upSeg.length > 1) traces.push({ net, layer: 'up', points: upSeg, widthMm: w });
  return { traces, vias };
}

function shoelace(pts: Pt[]): number {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    s += x1 * y2 - x2 * y1;
  }
  return s / 2;
}

/** Mutual-coupling-aware inductance of an N-turn planar circular spiral, in nH,
 *  via the Wheeler current-sheet approximation (Mohan et al. 1999 modified
 *  Wheeler; circular coefficients). This captures the N^2 mutual coupling of
 *  nested turns that a linear sum of single-loop closed forms misses.
 *
 *    L = (µ0/2)·N²·d_avg·c1·(ln(c2/ρ) + c3·ρ + c4·ρ²)
 *    d_avg = (d_out + d_in)/2      (mean diameter of the winding)
 *    ρ     = (d_out − d_in)/(d_out + d_in)   (fill ratio; 0 = single filament)
 *    circular current-sheet coefficients: c1=1.00, c2=2.46, c3=0.00, c4=0.20
 *
 *  d_in / d_out are the inner / outer COPPER-EDGE diameters of the winding, so a
 *  single turn (N=1) still has a finite ρ (= wire-width fill) rather than a
 *  ρ→0 log blow-up. Diameters are mm; result converted H→nH. */
export function estimateTxInductanceNH(nTurns: number, dInnerMm: number, dOuterMm: number): number {
  if (nTurns < 1) return 0;
  const mu0 = 4 * Math.PI * 1e-7;         // H/m
  const dIn = dInnerMm / 1000;            // mm -> m
  const dOut = dOuterMm / 1000;           // mm -> m
  const dAvg = (dOut + dIn) / 2;
  const rho = (dOut - dIn) / (dOut + dIn);
  if (rho <= 0) return 0;                 // degenerate (no radial extent)
  const c1 = 1.00, c2 = 2.46, c3 = 0.00, c4 = 0.20;   // circular current-sheet
  const lH = (mu0 / 2) * nTurns * nTurns * dAvg * c1 * (Math.log(c2 / rho) + c3 * rho + c4 * rho * rho);
  return lH * 1e9;                        // H -> nH
}

/** A single RX sin-coil built directly from the sinusoid (unrotated). The cos
 *  coil is produced from this by rotateSinToCos(). netBase is e.g. 'RX_FINE_SIN'.
 *
 *  `yieldsToPartner` marks THIS build as the one that will be rotated by π/(2p)
 *  into the cos coil. The sin and cos RX coils are DIFFERENT nets that share the
 *  same band on the same 'down' layer and cross each other geometrically at 4p
 *  azimuths per turn (their differential ribbons interleave a quarter period
 *  apart) — different-net copper coincident on 'down' is a fabrication short. The
 *  deterministic rule "cos yields to sin" resolves every such crossing: on the
 *  yielding coil BOTH conductors (r+ and r-) jog to 'up' through a via pair over
 *  the crossing span (the SAME machinery as the r+ self-crossing jog), so sin
 *  keeps the 'down' layer and cos hops over it. The inter-coil crossing sample
 *  indices are found analytically here (sign changes of the radial difference
 *  between this coil's curves and the partner coil's curves expressed in this
 *  coil's own pre-rotation frame; the partner = this coil rotated by −π/(2p),
 *  i.e. an index shift of −N/(4p)). Because they are computed in the own frame,
 *  the subsequent rigid rotation carries the jogs to exactly the right azimuths. */
function buildRxSinCoil(
  band: { rInMm: number; rOutMm: number },
  p: number,
  params: CoilParams,
  netBase: 'RX_FINE_SIN' | 'RX_COARSE_SIN',
  yieldsToPartner: boolean,
): { traces: CoilTrace[]; vias: CoilVia[]; terminals: Array<{ net: EncoderNet; at: Pt }>; areaMm2: number } {
  const { traceWidthMm: w, clearanceMm, rxTurns, datumRad } = params;
  const netP = `${netBase}_P` as EncoderNet;
  const netN = `${netBase}_N` as EncoderNet;

  const rMid = (band.rInMm + band.rOutMm) / 2;
  const A0 = (band.rOutMm - band.rInMm) / 2 - w / 2;
  const pitch = w + clearanceMm;

  const N = SEGMENTS_PER_PERIOD * p;         // samples per full turn (closed at i=N ≡ 0)
  // Sinusoid zeros land exactly on samples i = 16 + 32k (datum = -pi/2 puts the
  // first zero a quarter-period in). There are 2p of them per turn.
  const selfCrossings: number[] = [];
  for (let i = SEGMENTS_PER_PERIOD / 4; i < N; i += SEGMENTS_PER_PERIOD / 2) selfCrossings.push(i);

  // Per-sample sinusoid factor s(i) = sin(p·θ_i − datum). The two curves are
  // rMid ± A·s(i). Amplitude A cancels from every crossing condition, so the
  // crossing indices are the same for every turn.
  const sVal: number[] = [];
  for (let i = 0; i < N; i++) sVal.push(Math.sin(p * ((2 * Math.PI * i) / N) - datumRad));

  // Inter-coil crossings (only when this build becomes the yielding cos coil):
  // where either of this coil's conductors (own factors +s, −s) crosses either
  // of the partner sin coil's conductors as seen in this frame (partner = this
  // rotated by −rot, so its factors are s(j+rot), −s(j+rot)). Both of this
  // coil's conductors cross a partner conductor at every one of these azimuths,
  // so a single index set bridges BOTH r+ and r-.
  let interCrossings: number[] = [];
  if (yieldsToPartner) {
    const rot = Math.round(N / (4 * p));              // +π/(2p) as an integer sample shift = SEGMENTS_PER_PERIOD/4
    const cp = sVal.slice();                          // this coil r+ factor:  +s(j)
    const cm = sVal.map(v => -v);                     // this coil r- factor:  −s(j)
    const sp = sVal.map((_, j) => sVal[(j + rot) % N]);   // partner r+ in this frame: s(j+rot)
    const sm = sp.map(v => -v);                       // partner r- in this frame: −s(j+rot)
    const set = new Set<number>();
    for (const arr of [crossingIndices(cp, sp), crossingIndices(cp, sm), crossingIndices(cm, sp), crossingIndices(cm, sm)]) {
      arr.forEach(i => set.add(i));
    }
    interCrossings = [...set].sort((a, b) => a - b);
  }
  // r+ bridges at its self-crossings PLUS (yielding coil) the inter-coil
  // crossings; r- bridges only at the inter-coil crossings.
  const rPlusBridges = interCrossings.length
    ? [...new Set([...selfCrossings, ...interCrossings])].sort((a, b) => a - b)
    : selfCrossings;
  const rMinusBridges = interCrossings;

  const traces: CoilTrace[] = [];
  const vias: CoilVia[] = [];
  let areaMm2 = 0;

  const rAt = (A: number, i: number) => {
    const theta = (2 * Math.PI * i) / N;
    const s = Math.sin(p * theta - datumRad);
    return { plus: rMid + A * s, minus: rMid - A * s, theta };
  };
  const toXY = (r: number, theta: number): Pt => [r * Math.cos(theta), r * Math.sin(theta)];

  const firstTerminals: Array<{ net: EncoderNet; at: Pt }> = [];

  for (let k = 0; k < rxTurns; k++) {
    const A = A0 - k * pitch;

    // Precompute the two sampled curves for this turn (indexed mod N).
    const plusPts: Pt[] = [];
    const minusPts: Pt[] = [];
    for (let i = 0; i < N; i++) {
      const { plus, minus, theta } = rAt(A, i);
      plusPts.push(toXY(plus, theta));
      minusPts.push(toXY(minus, theta));
    }

    // --- signed net area: sum each lobe's shoelace WITH its winding sign -------
    // A lobe spans one crossing to the next (half a period = SEGMENTS_PER_PERIOD/2
    // samples), bounded by r+ (out) and r- (back). Consecutive lobes flip which
    // curve is outer, so the shoelace sign alternates and the sum ~ 0 (TX
    // rejection). Lobe endpoints land on crossings where r+ = r- = rMid, so the
    // closing edges are zero-length — no truncation seam. (Layer of a segment
    // does not affect its centreline, so the inter-coil jogs preserve this.)
    const lobeLen = SEGMENTS_PER_PERIOD / 2;
    for (const start of selfCrossings) {
      const lobe: Pt[] = [];
      for (let j = 0; j <= lobeLen; j++) lobe.push(plusPts[(start + j) % N]);
      for (let j = lobeLen; j >= 0; j--) lobe.push(minusPts[(start + j) % N]);
      areaMm2 += shoelace(lobe);
    }

    // --- r- conductor: 'down' (net N), lifted to 'up' only at inter-coil jogs.
    // Open walk 0..N-1 (its far end is picked up by the radial jumper below).
    const em = emitBridgedConductor(idx => minusPts[idx], 0, N - 1, rMinusBridges, netN, w, N);
    traces.push(...em.traces);
    vias.push(...em.vias);

    // --- r+ conductor: 'down' (net P), lifted to 'up' at self-crossings (hops
    // over r- inside this coil) and, on the yielding coil, at inter-coil jogs.
    // Closed walk 0..N (i=N wraps to the start point, closing r+).
    const ep = emitBridgedConductor(idx => plusPts[idx], 0, N, rPlusBridges, netP, w, N);
    traces.push(...ep.traces);
    vias.push(...ep.vias);

    // Series-return jumper joining the r+ half to the r- half at the far
    // turn-around (sample N-1). The two conductor ends sit at the winding's
    // amplitude PEAK (r+ = rMid+A, r- = rMid-A), and that peak azimuth is —
    // by the quarter-period construction — exactly the QUADRATURE PARTNER's
    // self-crossing azimuth, where the partner occupies BOTH layers near rMid
    // (its 'up' self-jog bridge + its 'down' r-). A plain radial jumper on
    // either single layer there shorts the partner (the old, deferred defect).
    //
    // Fix (co-design with the board router): route the jumper on 'down' along
    // the two clear band EDGES, dog-legging to a partner-clean azimuth
    // JUMPER_RELOCATE_SAMPLES off the peak, and lift ONLY the short middle span
    // that crosses the partner's near-rMid conductors to 'up'. A few samples off
    // the shared crossing the partner's two conductors have pulled apart to
    // rMid ± A·|cos(p·θ_c − datum)| and BOTH lie on 'down' (the partner's 'up'
    // self-jog bridge spans only ±JOG_SAMPLES around its crossing, and the
    // offset also clears the partner's inter-coil jogs). So:
    //   • 'down' arc along the OUTER band edge (r = r+_end) from the peak azimuth
    //     to the relocated azimuth θ_c — the edge is clear of the partner, which
    //     hugs rMid near its crossing;
    //   • 'down' radial in to just outside the partner's outer conductor;
    //   • a SHORT 'up' hop across the partner band [rMid−H, rMid+H] at θ_c — the
    //     partner is 'down'-only there, so a different-layer hop cannot short it,
    //     and the hop is a thin, localised obstacle (a ~4 mm window at ONE
    //     azimuth, vs the whole-band radial the earlier attempt used) that the
    //     board's in-family escape solver threads around;
    //   • 'down' radial out to the inner band edge, then a 'down' arc back along
    //     r = r−_end to the inner endpoint.
    // The hop half-span H = partnerOffset + traceWidth + clearance brackets both
    // partner conductors with clearance; it is derived from |cos| so it is
    // IDENTICAL for the sin build and the (rotated-into-cos) yielding build —
    // the cos coil stays a rigid rotation of the sin coil and quadrature holds
    // exactly. Endpoints (Pout/Pin at sample N-1) are unchanged, so series
    // continuity and the figure-eight topology are preserved. This REPLACES the
    // old deferred 'down' radial and lets the coil clearance invariant include
    // the jumper.
    const far = rAt(A, N - 1);
    const relocate = p > 1 ? FINE_JRS : COARSE_JRS;
    const jc = ((N - 1 - relocate) % N + N) % N;
    const thetaC = (2 * Math.PI * jc) / N;
    const partnerOffset = A * Math.abs(Math.cos(p * thetaC - datumRad));
    const H = Math.min(partnerOffset + HOP_MARGIN, A);
    const Pout = toXY(far.plus, far.theta);
    const Pin = toXY(far.minus, far.theta);
    const outerArc = arcAtRadius(far.plus, far.theta, thetaC);   // outer band edge, peak -> θ_c
    const innerArc = arcAtRadius(far.minus, thetaC, far.theta);  // inner band edge, θ_c -> peak
    const hopHi = toXY(rMid + H, thetaC);
    const hopLo = toXY(rMid - H, thetaC);
    const cIn = toXY(far.minus, thetaC);
    traces.push({ net: netP, layer: 'down', widthMm: w, points: [Pout, ...outerArc.slice(1), hopHi] });
    traces.push({ net: netP, layer: 'up', widthMm: w, points: [hopHi, hopLo] });
    traces.push({ net: netP, layer: 'down', widthMm: w, points: [hopLo, cIn, ...innerArc.slice(1), Pin] });
    vias.push({ net: netP, at: hopHi, drillMm: VIA_DRILL_MM, padMm: VIA_PAD_MM });
    vias.push({ net: netP, at: hopLo, drillMm: VIA_DRILL_MM, padMm: VIA_PAD_MM });

    if (k === 0) {
      const start = rAt(A, 0);
      firstTerminals.push({ net: netP, at: toXY(start.plus, start.theta) });
      firstTerminals.push({ net: netN, at: toXY(start.minus, start.theta) });
    }
  }

  return { traces, vias, terminals: firstTerminals, areaMm2 };
}

/** Rotate a whole coil (points + via/terminal positions) by `angle` and remap
 *  its nets SIN -> COS. Quadrature-by-rotation guarantees the 1e-6 cos test. */
function rotateSinToCos(
  coil: { traces: CoilTrace[]; vias: CoilVia[]; terminals: Array<{ net: EncoderNet; at: Pt }> },
  angle: number,
): { traces: CoilTrace[]; vias: CoilVia[]; terminals: Array<{ net: EncoderNet; at: Pt }> } {
  const ca = Math.cos(angle);
  const sa = Math.sin(angle);
  const rp = ([x, y]: Pt): Pt => [x * ca - y * sa, x * sa + y * ca];
  const remap = (net: EncoderNet): EncoderNet => net.replace('_SIN', '_COS') as EncoderNet;
  return {
    traces: coil.traces.map(t => ({ net: remap(t.net), layer: t.layer, widthMm: t.widthMm, points: t.points.map(rp) })),
    vias: coil.vias.map(v => ({ net: remap(v.net), at: rp(v.at), drillMm: v.drillMm, padMm: v.padMm })),
    terminals: coil.terminals.map(t => ({ net: remap(t.net), at: rp(t.at) })),
  };
}

/** The TX excitation coil: a nested planar SPIRAL of N concentric turns, all on
 *  the 'down' layer and all radially OUTSIDE the fine RX band (so no TX copper
 *  shares a radius with an RX annulus on 'down' — that would be a galvanic short).
 *  Because a spiral encloses the full sensing area, no inner return conductor is
 *  needed (the old inner circle at r<hubFloor is gone); the spiral's inner
 *  terminus is carried back out to the chip by a single radial bridge on 'up',
 *  which also stays outside every RX band.
 *
 *  SERIES CONTINUITY (fix 2): the 'down' copper is ONE continuous polyline — the
 *  drive current enters at the outer terminal, traverses every turn in series,
 *  and exits through the inner-terminus via. No turn is a closed circle (each
 *  omits its final arc segment, bridged by the inward diagonal to the next
 *  turn), so there are no shorted parasitic rings loading the 4 MHz tank.
 *
 *  Turn count is the smallest N whose Wheeler current-sheet inductance reaches the
 *  chip's mid-window LC-tank target, capped so the outer turn fits the TX wrap.
 *  Returns geometry (traces + return-bridge vias) plus the Wheeler estimate. */
function buildTxLoop(
  bands: EncoderBands,
  params: CoilParams,
): { traces: CoilTrace[]; vias: CoilVia[]; terminals: Array<{ net: EncoderNet; at: Pt }>; inductanceNH: number } {
  const { traceWidthMm: w, clearanceMm } = params;
  const pitch = w + clearanceMm;

  // Nominal tank inductance from the chip's carrier + split-cap (mid-window):
  //   L = 1 / ((2π f)^2 · (CTx/2)).  Landing here centres the carrier at ~4 MHz.
  const cEffF = (CHIP.txTankCapPF * 1e-12) / 2;
  const targetNH = 1e9 / (Math.pow(2 * Math.PI * CHIP.carrierHz, 2) * cEffF);

  // Innermost turn sits outside the fine RX band with the inner-terminus VIA PAD
  // budgeted into the clearance (pad radius > trace half-width), so the pad's
  // inner edge — not just the trace edge — clears the band copper by >= clearance.
  // Turns grow OUTWARD only, capped so the outer copper edge stays within the TX
  // wrap (bands.tx.rOutMm). This keeps every 'down' turn clear of the RX annulus.
  const rStart = bands.fine.rOutMm + clearanceMm + Math.max(w / 2, VIA_PAD_MM / 2);
  const rCapCenter = bands.tx.rOutMm - w / 2;
  const maxTurns = Math.max(1, Math.floor((rCapCenter - rStart) / pitch) + 1);

  // Wheeler diameters use the winding's inner/outer COPPER EDGES. The diagonal
  // turn-transitions stay radially between adjacent turns, so the real copper
  // envelope is exactly [rStart - w/2, rStart + (n-1)·pitch + w/2].
  const wheelerFor = (n: number): number =>
    estimateTxInductanceNH(n, 2 * (rStart - w / 2), 2 * (rStart + (n - 1) * pitch + w / 2));

  let nTurns = 1;
  let inductanceNH = wheelerFor(1);
  for (let n = 1; n <= maxTurns; n++) {
    nTurns = n;
    inductanceNH = wheelerFor(n);
    if (inductanceNH >= targetNH) break;        // reached the mid-window target
  }

  // --- 'down' spiral: ONE continuous series polyline, outer turn -> inner turn
  //     (fix 2: series continuity). Turn k walks samples i = 0..SAMPLES-1,
  //     deliberately OMITTING the closing arc segment back to θ = 0; the step to
  //     the next (inner) turn's i = 0 point (θ = 0 ≡ 2π) is a short inward
  //     diagonal that spans exactly the omitted 2π/SAMPLES arc while dropping one
  //     pitch. No turn is a closed loop, so Kirchhoff-wise the drive current
  //     traverses every turn in series — no shorted parasitic rings damping the
  //     LC tank. Deterministic index loop.
  const downPts: Pt[] = [];
  for (let k = nTurns - 1; k >= 0; k--) {
    const r = rStart + k * pitch;
    for (let i = 0; i < TX_CIRCLE_SAMPLES; i++) {
      const theta = (2 * Math.PI * i) / TX_CIRCLE_SAMPLES;
      downPts.push([r * Math.cos(theta), r * Math.sin(theta)]);
    }
  }
  const traces: CoilTrace[] = [{ net: 'TX_P', layer: 'down', points: downPts, widthMm: w }];

  // --- 'up' return bridge: carries the spiral's inner terminus (the walk's last
  //     point) radially back out over the outer turns to the chip side. Entirely
  //     outside the RX bands (r ∈ [rStart, rOuter]).
  const rOuter = rStart + (nTurns - 1) * pitch;
  const innerEnd: Pt = downPts[downPts.length - 1];
  const thetaEnd = (2 * Math.PI * (TX_CIRCLE_SAMPLES - 1)) / TX_CIRCLE_SAMPLES;
  const bridgeOuter: Pt = [rOuter * Math.cos(thetaEnd), rOuter * Math.sin(thetaEnd)];
  traces.push({ net: 'TX_N', layer: 'up', points: [innerEnd, bridgeOuter], widthMm: w });

  // Via at the inner terminus (down spiral -> up bridge layer change).
  const vias: CoilVia[] = [
    { net: 'TX_N', at: innerEnd, drillMm: VIA_DRILL_MM, padMm: VIA_PAD_MM },
  ];

  const terminals: Array<{ net: EncoderNet; at: Pt }> = [
    { net: 'TX_P', at: downPts[0] },            // outer turn, θ = 0: walk entry
    { net: 'TX_N', at: bridgeOuter },           // up-bridge outer end: walk exit
  ];
  return { traces, vias, terminals, inductanceNH };
}

/** Build the complete dual-track sensing element: fine + coarse SIN/COS RX coils
 *  (each a differential figure-eight winding) plus the multi-turn TX loop. */
export function buildSensingElement(
  bands: EncoderBands,
  periods: { fine: number; coarse: number },
  p: CoilParams,
): SensingElement {
  // Each track: build the sin coil, then build a SEPARATE (yielding) copy that
  // carries the inter-coil crossover jogs and rotate it by π/(2p) into the cos
  // coil. The cos build must be its own instance (not a rotation of `*Sin`)
  // because it hops to 'up' at the sin×cos crossings — geometry the sin coil
  // does not have. Quadrature is still exact: both builds share the identical
  // centreline sampling, and the jogs only change a segment's layer, not its
  // vertices, so rotating the cos build reproduces the sin centreline exactly.
  const fineSin = buildRxSinCoil(bands.fine, periods.fine, p, 'RX_FINE_SIN', false);
  const fineCos = rotateSinToCos(buildRxSinCoil(bands.fine, periods.fine, p, 'RX_FINE_SIN', true), Math.PI / (2 * periods.fine));
  const coarseSin = buildRxSinCoil(bands.coarse, periods.coarse, p, 'RX_COARSE_SIN', false);
  const coarseCos = rotateSinToCos(buildRxSinCoil(bands.coarse, periods.coarse, p, 'RX_COARSE_SIN', true), Math.PI / (2 * periods.coarse));
  const tx = buildTxLoop(bands, p);

  const traces: CoilTrace[] = [
    ...fineSin.traces, ...fineCos.traces,
    ...coarseSin.traces, ...coarseCos.traces,
    ...tx.traces,
  ];
  const vias: CoilVia[] = [
    ...fineSin.vias, ...fineCos.vias,
    ...coarseSin.vias, ...coarseCos.vias,
    ...tx.vias,
  ];
  const terminals = [
    ...fineSin.terminals, ...fineCos.terminals,
    ...coarseSin.terminals, ...coarseCos.terminals,
    ...tx.terminals,
  ];

  return {
    traces,
    vias,
    terminals,
    txInductanceEstNH: tx.inductanceNH,
    rxNetAreaMm2: {
      fineSin: fineSin.areaMm2,
      fineCos: fineSin.areaMm2,    // rotation preserves signed area exactly
      coarseSin: coarseSin.areaMm2,
      coarseCos: coarseSin.areaMm2,
    },
  };
}
